import axios from 'axios';
import momentyz from 'moment-timezone';

const STORAGE = {
	token: 'partner_token',
	refresh: 'partner_refresh',
	expired: 'partner_expired',
};

const state = {
	token: localStorage.getItem(STORAGE.token) || '',
	refresh: localStorage.getItem(STORAGE.refresh) || '',
	expired: localStorage.getItem(STORAGE.expired) || '',
	id: '',
	profile: '',
	partner: null,
	banners: [],
};

const getters = {
	getToken: state => state.token,
	getRefresh: state => state.refresh,
	getExpired: state => state.expired,
	getPartner: state => state.partner,
	getBanners: state => state.banners,
	isAuthenticated: state => {
		if (!state.token || !state.expired) {
			return false;
		}
		momentyz.tz.setDefault('Europe/Rome');
		const dateexpired = momentyz.utc(momentyz.unix(state.expired));
		const datenow = momentyz.utc(new Date());
		return datenow <= dateexpired;
	},
};

const mutations = {
	setToken: (state, value) => {
		state.token = value;
		if (value) localStorage.setItem(STORAGE.token, value);
		else localStorage.removeItem(STORAGE.token);
	},
	setRefresh: (state, value) => {
		state.refresh = value;
		if (value) localStorage.setItem(STORAGE.refresh, value);
		else localStorage.removeItem(STORAGE.refresh);
	},
	setExpired: (state, value) => {
		state.expired = value;
		if (value) localStorage.setItem(STORAGE.expired, value);
		else localStorage.removeItem(STORAGE.expired);
	},
	setId: (state, value) => {
		state.id = value;
	},
	setProfile: (state, value) => {
		state.profile = value;
	},
	setPartner: (state, value) => {
		state.partner = value;
	},
	setBanners: (state, value) => {
		state.banners = value || [];
	},
	// token nuovi dopo cambio password / "esci da tutti gli altri dispositivi": i vecchi sono revocati
	setSession: (state, session) => {
		if (!session || !session.accessToken) {
			return;
		}
		state.token = session.accessToken;
		state.refresh = session.refreshToken;
		state.expired = session.expired;
		localStorage.setItem(STORAGE.token, session.accessToken);
		localStorage.setItem(STORAGE.refresh, session.refreshToken);
		localStorage.setItem(STORAGE.expired, session.expired);
	},
	setLogout: state => {
		state.token = '';
		state.refresh = '';
		state.expired = '';
		state.id = '';
		state.profile = '';
		state.partner = null;
		state.banners = [];
		localStorage.removeItem(STORAGE.token);
		localStorage.removeItem(STORAGE.refresh);
		localStorage.removeItem(STORAGE.expired);
	},
};

const actions = {
	login: async (context, payload) => {
		const url = 'partner-auth/login';
		try {
			const response = await axios.post(`${context.rootGetters['settings/getUrl']}${url}`, payload);
			if (response.status === 201) {
				context.commit('setToken', response.data.accessToken);
				context.commit('setRefresh', response.data.refreshToken);
				context.commit('setExpired', response.data.expired);
				context.commit('setId', response.data.id);
				context.commit('setProfile', response.data.profile);
			}
			return response;
		} catch (error) {
			return error.response || { status: 500, data: { message: 'network_error' } };
		}
	},
	refreshToken: async context => {
		const url = 'partner-auth/token';
		const refresh = context.state.refresh || localStorage.getItem(STORAGE.refresh);
		if (!refresh) {
			return { status: false };
		}
		try {
			const response = await axios.post(`${context.rootGetters['settings/getUrl']}${url}`, { token: refresh });
			if (response.status === 201) {
				context.commit('setToken', response.data.accessToken);
				context.commit('setExpired', response.data.expired);
				context.commit('setProfile', response.data.profile);
				return { status: true, token: response.data.accessToken };
			}
			return { status: false };
		} catch (error) {
			return { status: false };
		}
	},
	ensureFreshToken: async context => {
		if (!context.state.token) {
			return false;
		}
		momentyz.tz.setDefault('Europe/Rome');
		const dateexpired = momentyz.utc(momentyz.unix(context.state.expired));
		const datenow = momentyz.utc(new Date());
		if (datenow > dateexpired) {
			const result = await context.dispatch('refreshToken');
			return !!result.status;
		}
		return true;
	},
	fetchMe: async context => {
		const ok = await context.dispatch('ensureFreshToken');
		if (!ok) {
			context.commit('setLogout');
			return null;
		}
		const url = 'partner-program/me';
		try {
			const response = await axios.get(`${context.rootGetters['settings/getUrl']}${url}`, {
				headers: { Authorization: `Bearer ${context.state.token}` },
			});
			if (response.status === 201) {
				context.commit('setPartner', response.data.data);
				return response.data.data;
			}
			return null;
		} catch (error) {
			if (error.response && error.response.status === 401) {
				context.commit('setLogout');
			}
			return null;
		}
	},
	fetchBanners: async context => {
		const ok = await context.dispatch('ensureFreshToken');
		if (!ok) {
			return [];
		}
		const url = 'partner-program/banners';
		try {
			const response = await axios.get(`${context.rootGetters['settings/getUrl']}${url}`, {
				headers: { Authorization: `Bearer ${context.state.token}` },
			});
			if (response.status === 201) {
				const list = (response.data && response.data.data) || [];
				context.commit('setBanners', list);
				return list;
			}
			return [];
		} catch (error) {
			return [];
		}
	},
	fetchLinksCount: async (context, payload = {}) => {
		const ok = await context.dispatch('ensureFreshToken');
		if (!ok) {
			return 0;
		}
		const url = 'partner-program/links';
		try {
			const response = await axios.get(`${context.rootGetters['settings/getUrl']}${url}`, {
				headers: { Authorization: `Bearer ${context.state.token}` },
				params: { from: payload.from || '', to: payload.to || '' },
			});
			if (response.status === 201) {
				return (response.data && response.data.data && response.data.data.total) || 0;
			}
			return 0;
		} catch (error) {
			return 0;
		}
	},
	updateProfile: async (context, payload) => {
		const ok = await context.dispatch('ensureFreshToken');
		if (!ok) {
			return { success: false };
		}
		const url = 'partner-program/profile';
		try {
			const response = await axios.patch(`${context.rootGetters['settings/getUrl']}${url}`, payload, {
				headers: { Authorization: `Bearer ${context.state.token}` },
			});
			if (response.status === 201) {
				context.commit('setPartner', response.data.data);
				return { success: true };
			}
			return { success: false, message: response.data && response.data.message };
		} catch (error) {
			return { success: false, message: error.response && error.response.data && error.response.data.message };
		}
	},
	changePassword: async (context, payload) => {
		const ok = await context.dispatch('ensureFreshToken');
		if (!ok) {
			return { success: false };
		}
		const url = 'partner-program/password';
		try {
			const response = await axios.patch(`${context.rootGetters['settings/getUrl']}${url}`, payload, {
				headers: { Authorization: `Bearer ${context.state.token}` },
			});
			if (response.status === 201) {
				context.commit('setSession', response.data && response.data.data);
				return { success: true };
			}
			return { success: false, message: response.data && response.data.message };
		} catch (error) {
			return { success: false, message: error.response && error.response.data && error.response.data.message };
		}
	},
	logoutAll: async context => {
		const ok = await context.dispatch('ensureFreshToken');
		if (!ok) {
			return { success: false };
		}
		const url = 'partner-program/logout-all';
		try {
			const response = await axios.post(`${context.rootGetters['settings/getUrl']}${url}`, {}, {
				headers: { Authorization: `Bearer ${context.state.token}` },
			});
			if (response.status === 201) {
				context.commit('setSession', response.data && response.data.data);
				return { success: true };
			}
			return { success: false };
		} catch (error) {
			return { success: false, message: error.response && error.response.data && error.response.data.message };
		}
	},
	logout: context => {
		context.commit('setLogout');
	},
};

export default {
	namespaced: true,
	state,
	mutations,
	getters,
	actions,
};
